/**
 * PREFERENCES as a `SurfaceController`, driving both places the panel appears:
 * the `/settings` route (`routes/Settings.tsx`, reached from the Main Menu's
 * `OTHER OPTIONS -> PREFERENCES`) and the in-flight overlay `O` opens
 * (`flight/PreferencesOverlay.tsx`).
 *
 * One controller for both is the point. The two surfaces differ in exactly two
 * things — where the assets come from and what ACCEPT does — and both are
 * parameters here, so the panel's behaviour cannot drift between the menus and
 * the cockpit.
 *
 * It holds the settings while it is open and pushes each change straight out
 * through `PreferenceSinks`: in the shell those write to storage, in flight
 * they are the route's own live-apply functions, so a setting changed over a
 * paused sortie is already in effect when the panel closes.
 */

import type { SettingsStore } from "../audio/settings.ts";
import {
  applyPreferencesCommand,
  dispatchPreferences,
  loadPreferences,
  type PreferenceSinks,
  type Preferences,
} from "../mission/preferences.ts";
import { ControlsSection, type GamepadFeed } from "./controls-surface.ts";
import type { MenuAction } from "./model/menu-model.ts";
import { enhancedIndex } from "../three/enhanced-catalog.ts";
import { preferencesScreen } from "./model/preferences-screen.ts";
import { plannedRendererFacts } from "../three/renderer-plan.ts";
import { realismScreen } from "./model/realism-screen.ts";
import type { ShellDialog, ShellScreen } from "./model/shell-screen.ts";
import { CHECK_SHEET } from "./render/widget-ops.ts";
import { PANEL_SHEET } from "./render/panel-chrome.ts";
import type { SurfaceController } from "./screen-runtime.ts";

export interface PreferencesSurfaceOptions {
  readonly store: SettingsStore | undefined;
  /** Where a changed group goes — storage in the shell, the live scene in flight. */
  readonly sinks: PreferenceSinks;
  /**
   * ACCEPT (and `Escape`). Returns a route path when the surface should leave —
   * the shell's `/settings` goes back to the Main Menu — or `undefined` when
   * the caller has closed the panel itself, which is what the flight overlay
   * does.
   */
  readonly onClose: () => string | undefined;
  readonly inFlight?: boolean;
  /**
   * The live joystick, for the CONTROLS section (`controls-surface.ts`).
   * Optional: a surface without one shows the panel with no device on it,
   * which is exactly what a player with no stick should see.
   */
  readonly gamepad?: GamepadFeed;
}

/**
 * Which screen this surface is showing. The Realism Panel is the original's own
 * and is left exactly as measured; CONTROLS is a port screen with three screens
 * of its own behind it, which `ControlsSection` owns.
 */
type PreferencesView = "preferences" | "realism" | "controls";

class PreferencesSurface implements SurfaceController {
  /** The panel chrome's sprite sheet and the checkbox tick; no screen background — the panel paints its own. */
  readonly assets = { images: [], sprites: [PANEL_SHEET, CHECK_SHEET] };

  private prefs: Preferences;
  /** What RESTORE goes back to: the settings this visit opened with, the same reading `applyRealismCommand` gives it. */
  private opening: Preferences;
  private view: PreferencesView = "preferences";
  private cursor = 0;
  private readonly controls: ControlsSection;

  constructor(private readonly options: PreferencesSurfaceOptions) {
    this.prefs = loadPreferences(options.store);
    this.opening = this.prefs;
    this.controls = new ControlsSection(options.gamepad);
  }

  /**
   * Re-reads storage and returns to the front screen. The flight overlay calls
   * this every time it opens, because a setting can have moved while it was
   * shut — `V` mutes the audio without the panel being involved — and the
   * panel must never show a stale value it would then write back.
   */
  reopen(): void {
    this.prefs = loadPreferences(this.options.store);
    this.opening = this.prefs;
    this.view = "preferences";
    this.cursor = 0;
    this.controls.reset();
  }

  screen(): ShellScreen {
    if (this.view === "realism") return realismScreen({ panel: this.prefs.realism, inFlight: this.options.inFlight });
    if (this.view === "controls") return this.controls.screen();
    return preferencesScreen({
      prefs: this.prefs,
      inFlight: this.options.inFlight,
      // `undefined` is "the manifest fetch has not landed", which must not flash
      // UNAVAILABLE at a pilot who has the models — only a *resolved* empty
      // index is an answer (`EffectsToggles.tsx` states the same rule for the
      // in-flight switch).
      enhancedModelsAvailable: enhancedIndex()?.size !== 0,
      // Re-read on every paint rather than captured at construction: the stored
      // record this is derived from is *written by a failed boot*, so a panel
      // opened after one has to print what happened, not what was true when the
      // shell started (`three/renderer-plan.ts`).
      renderer: plannedRendererFacts(),
    });
  }

  /** True while the panel is showing a screen that has to watch a live device. */
  needsPolling(): boolean {
    return this.view === "controls";
  }

  /** One poll of the joystick; `true` when the screen changed and wants a repaint. */
  poll(): boolean {
    return this.view === "controls" ? this.controls.poll() : false;
  }

  dialog(): ShellDialog | undefined {
    return undefined;
  }

  focus(): number {
    return this.cursor;
  }

  setFocus(index: number): void {
    this.cursor = index;
  }

  setDialog(): void {
    // No screen here opens one; the panel's every control is on the screen itself.
  }

  typeKey(): boolean {
    return false;
  }

  private show(view: PreferencesView): undefined {
    this.view = view;
    this.cursor = 0;
    return undefined;
  }

  run(action: MenuAction | undefined): string | undefined {
    if (!action) return undefined;
    if (action.kind === "navigate") return action.path;
    if (action.kind !== "command") return undefined;
    if (action.command === "pref:realism") return this.show("realism");
    if (action.command === "pref:controls") return this.show("controls");
    if (this.view === "controls") {
      const outcome = this.controls.run(action.command);
      if (outcome === "back") return this.show("preferences");
      if (outcome === "handled") return undefined;
    }
    // The Realism Panel's own ACCEPT, and `Escape` on it: back to PREFERENCES
    // rather than out of the surface, because that is where it was opened from.
    if (action.command === "realism-accept") return this.show("preferences");
    if (action.command === "pref:accept") {
      return this.view === "preferences" ? this.options.onClose() : this.show("preferences");
    }
    const next = applyPreferencesCommand(this.prefs, action.command, this.opening);
    if (!next) return undefined;
    dispatchPreferences(this.prefs, next, this.options.sinks);
    this.prefs = next;
    return undefined;
  }
}

export function createPreferencesSurface(options: PreferencesSurfaceOptions): PreferencesSurface {
  return new PreferencesSurface(options);
}

export type { PreferencesSurface };
